import { Fragment } from "react";
import { combineVerifications } from "shared/utils";
import { VerificationNames } from "shared/types";
import { VerificationSchemaNames } from "types/landlord/verificationTemplate";
import { Cell } from "./Cell";
import { WithIcon } from "./WithIcon";
import { VERIFICATION_ICON, VERIFICATIONS_ORDER } from "../constants";
import { Verification } from "../types";
import {
  shouldShowLandlordVerification,
  sortVerifications,
  verificationNameToTitle
} from "../utils";

const HeaderIconCell = ({
  name,
  muted = false
}: {
  name: VerificationNames;
  muted?: boolean;
}) => (
  <Cell className="bg-background-900">
    <WithIcon
      Icon={VERIFICATION_ICON[name]}
      className={muted ? "stroke-content-100" : undefined}
    >
      {muted ? (
        <p className="text-content-300">{verificationNameToTitle(name)}</p>
      ) : (
        verificationNameToTitle(name)
      )}
    </WithIcon>
  </Cell>
);

const IncomeHistoryHeader = () => (
  <>
    <Cell className="bg-background-900">
      <WithIcon
        Icon={VERIFICATION_ICON[VerificationNames.IncomeVerification]}
        className="stroke-content-100"
      >
        <p className="text-content-300">Income history</p>
      </WithIcon>
    </Cell>
    <Cell isDataCell>
      <p>
        Gross income
        <span className="text-content-100"> (Verified)</span>
      </p>
    </Cell>
    <Cell isDataCell>
      <p>
        Net income
        <span className="text-content-100"> (Verified)</span>
      </p>
    </Cell>
    <Cell isDataCell>
      <p>
        Gross income
        <span className="text-content-100"> (Self-reported)</span>
      </p>
    </Cell>
  </>
);

const RentalApplicationHeader = () => (
  <>
    <HeaderIconCell name={VerificationNames.RentalApplication} muted />
    <Cell isDataCell>Dependents</Cell>
    <Cell isDataCell>Vehicles</Cell>
    <Cell isDataCell>Pets or service animals</Cell>
  </>
);

const HousingHistoryHeader = ({
  showLandlordVerification
}: {
  showLandlordVerification: boolean;
}) => (
  <>
    <HeaderIconCell name={VerificationNames.HousingHistory} />
    <Cell isDataCell>Number of residences</Cell>
    <Cell isDataCell>Average monthly payments</Cell>
    <Cell isDataCell>Total months of residence</Cell>
    {showLandlordVerification ? (
      <Cell isDataCell>Landlord verification</Cell>
    ) : null}
  </>
);

const PetScreeningHeader = () => (
  <>
    <HeaderIconCell name={VerificationNames.PetScreeningReport} />
    <Cell isDataCell>Pets / Assistance animals</Cell>
  </>
);

const IdentityVerificationHeader = () => (
  <>
    <HeaderIconCell name={VerificationNames.IdentityVerification} muted />
    <Cell isDataCell>Identity verification</Cell>
  </>
);

const CreditReportHeader = ({ collapsed }: { collapsed?: boolean }) => (
  <>
    <HeaderIconCell name={VerificationNames.CreditReport} />
    {collapsed ? (
      <Cell isDataCell>Report</Cell>
    ) : (
      <>
        <Cell
          isDataCell
          className="flex items-center pl-0 pr-0 relative"
          isFirst
        >
          Credit score
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Past due accounts
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Balance past due
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Balance of collections
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Bankruptcy
        </Cell>
      </>
    )}
  </>
);

const EvictionReportHeader = () => (
  <>
    <HeaderIconCell name={VerificationNames.EvictionReport} />
    <Cell isDataCell className="flex items-center pl-0 pr-0 relative" isFirst>
      Events reported
    </Cell>
  </>
);

const CriminalReportHeader = ({
  hasClearaCategorization
}: {
  hasClearaCategorization?: boolean;
}) => (
  <>
    <HeaderIconCell name={VerificationNames.CriminalReport} />
    {hasClearaCategorization ? (
      <>
        <Cell isDataCell>Felony</Cell>
        <Cell isDataCell>Misdemeanor</Cell>
        <Cell isDataCell>Other criminal</Cell>
      </>
    ) : (
      <>
        <Cell isDataCell>Criminal</Cell>
        <Cell isDataCell>Criminal traffic</Cell>
      </>
    )}
    <Cell isDataCell>National sex offender </Cell>
    <Cell isDataCell>OFAC</Cell>
  </>
);

const CanadianCriminalReportHeader = () => (
  <>
    <HeaderIconCell name={VerificationNames.CanadianCriminalReport} />
    <Cell isDataCell>Report</Cell>
  </>
);

const CanadianCreditReportHeader = ({ collapsed }: { collapsed?: boolean }) => (
  <>
    <HeaderIconCell name={VerificationNames.CanadianCreditReport} />
    {collapsed ? (
      <Cell isDataCell>Report</Cell>
    ) : (
      <>
        <Cell
          isDataCell
          className="flex items-center pl-0 pr-0 relative"
          isFirst
        >
          Credit score
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Past due accounts
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Balance past due
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Balance of collections
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Bankruptcy
        </Cell>
      </>
    )}
  </>
);

const WarningsReportHeader = () => (
  <>
    <HeaderIconCell name={VerificationNames.WarningsReport} />
    <Cell isDataCell>Warning signals detected</Cell>
  </>
);

interface VerificationHeaderProps {
  verification: Verification;
  hasClearaCategorization?: boolean;
  showLandlordVerification: boolean;
  allUsCreditSkipped?: boolean;
  allCaCreditSkipped?: boolean;
}

const VerificationHeader = ({
  verification,
  hasClearaCategorization,
  showLandlordVerification,
  allUsCreditSkipped,
  allCaCreditSkipped
}: VerificationHeaderProps) => {
  switch (verification.name) {
    case VerificationNames.RentalApplication:
      return <RentalApplicationHeader />;
    case VerificationNames.HousingHistory:
      return (
        <HousingHistoryHeader
          showLandlordVerification={showLandlordVerification}
        />
      );
    case VerificationNames.PetScreeningReport:
      return <PetScreeningHeader />;
    case VerificationNames.IdentityVerification:
      return <IdentityVerificationHeader />;
    case VerificationNames.CreditReport:
      return <CreditReportHeader collapsed={allUsCreditSkipped} />;
    case VerificationNames.EvictionReport:
      return <EvictionReportHeader />;
    case VerificationNames.CriminalReport:
      return (
        <CriminalReportHeader
          hasClearaCategorization={hasClearaCategorization}
        />
      );
    case VerificationNames.CanadianCriminalReport:
      return <CanadianCriminalReportHeader />;
    case VerificationNames.CanadianCreditReport:
      return <CanadianCreditReportHeader collapsed={allCaCreditSkipped} />;
    case VerificationNames.WarningsReport:
      return <WarningsReportHeader />;
    default:
      return null;
  }
};

interface VerificationHeaderCellsProps {
  availableVerificationNames: VerificationNames[];
  verificationSchemas?: VerificationSchemaNames;
  hasClearaCategorization: boolean;
  allUsCreditSkipped?: boolean;
  allCaCreditSkipped?: boolean;
}

export const VerificationHeaderCells = ({
  availableVerificationNames,
  verificationSchemas,
  hasClearaCategorization,
  allUsCreditSkipped,
  allCaCreditSkipped
}: VerificationHeaderCellsProps) => {
  const verifications = combineVerifications(
    sortVerifications(
      availableVerificationNames.map((name) => ({
        id: name,
        name: name as VerificationNames
      })) as unknown[] as Verification[],
      VERIFICATIONS_ORDER
    ),
    [
      [
        VerificationNames.IncomeVerification,
        VerificationNames.VerifiedIncomeVerification,
        VerificationNames.CombinedIncome
      ]
    ]
  );

  return (
    <>
      {verifications.map((v) => {
        if (Array.isArray(v))
          return (
            <Fragment key={v[0].id}>
              <IncomeHistoryHeader />
            </Fragment>
          );
        return (
          <VerificationHeader
            key={v.id}
            verification={v}
            hasClearaCategorization={hasClearaCategorization}
            allUsCreditSkipped={allUsCreditSkipped}
            allCaCreditSkipped={allCaCreditSkipped}
            showLandlordVerification={shouldShowLandlordVerification(
              verificationSchemas
            )}
          />
        );
      })}
    </>
  );
};
